import { useCallback, useMemo } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import {
  authTokenAtom,
  languageOptionsAtom,
  selectedTextForPlayAtom,
  selectedValuesAtom,
  setNotificationAtom,
  shouldCloseMenuAtom,
  styleEmotionNames,
  userInputAtom,
  validationErrorsAtom,
  voicesDescriptionAtom,
} from "atoms";
import { useAtomValue, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { getPhoneme, updateWavHeader } from "shared/utils/tts";
import {
  getWrappedInput,
  getSanitizedInput,
  cleanupSSML,
  sanitizePhInSsml,
  stripSpeakTags,
} from "shared/utils/ssml-helper";
import {
  checkInvalidNestedTag,
  checkInvalidNestedTagForProsody,
} from "shared/utils/tts-custom-editor";
import { removeParagraphTags } from "shared/utils/tts-editor-helper";
import {
  isQuotaExhaustedError,
  extractQuotaInfo,
  isUsagePeriodExpired,
} from "shared/utils/grpc-error-handler";
import { useTTSGRPCService } from "./useTTSGRPCService";
import { validateSSML } from "features/TTS/TTSCustomEditor/utils/ssmlValidator";
import { runtimeConfig } from "@/shared/config/runtime-config-loader";

interface TTSHelper {
  getTTSRequestUrl: (
    shouldUseFullText: boolean,
    customSampleRate?: string,
    finalText?: string,
    voiceOverride?: string,
    languageOverride?: string
  ) => Promise<{ url: string; fromCache: boolean }>;
  streamTTSAudio: (
    shouldUseFullText: boolean,
    callbacks: {
      onPlaybackStarted: () => void;
      onPlaybackEnded: () => void;
    },
    customSampleRate?: string,
    finalText?: string,
    voiceOverride?: string,
    languageOverride?: string
  ) => Promise<{
    stop: () => void;
    pause: () => void;
    resume: () => void;
    isPaused: () => boolean;
    analyser: AnalyserNode;
  }>;
  checkIsNestingValid: (
    editorRef: React.RefObject<EditorRef | null>,
    tagToCheck: string,
    isProsody: boolean,
    tagNameForError?: string
  ) => boolean;
  getPhonemeText: (input: string) => Promise<string>;
}

export function useTTS(): TTSHelper {
  const token = useAtomValue(authTokenAtom);
  const userInput = useAtomValue(userInputAtom);
  const selectedTextForPlay = useAtomValue(selectedTextForPlayAtom);
  const setNotification = useSetAtom(setNotificationAtom);
  const setShouldCloseMenu = useSetAtom(shouldCloseMenuAtom);
  const setValidationErrors = useSetAtom(validationErrorsAtom);

  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const voiceNames = voicesDescription.map(v => v.voiceName);
  const emotionNames = useAtomValue(styleEmotionNames);
  const languageOptions = useAtomValue(languageOptionsAtom);
  const languageValues = useMemo(() => languageOptions.map(o => o.value), [languageOptions]);

  const { t } = useTranslation();
  const { synthesize, synthesizeStream, checkCache, setCache } = useTTSGRPCService();
  const {
    selectedLanguage,
    selectedVoice,
    selectedSampleRate,
    selectedEnv,
    selectedDeployment,
  } = useAtomValue(selectedValuesAtom);

  const setErrorMessage = useCallback((message: string) => {
    setNotification({
      message,
      severity: "error",
    });
  }, [setNotification]);


  const prepareSSMLText = useCallback(
    (
      shouldUseFullText: boolean,
      finalText = "",
    ): string => {
      const rawText =
        selectedTextForPlay && !shouldUseFullText
          ? selectedTextForPlay
          : userInput;
      const afterNbspReplacement = rawText.replaceAll("&nbsp;", "");
      let cleanedText = removeParagraphTags(afterNbspReplacement);

      const textContent = cleanedText
        .replace(/<[^>]*>/g, "")
        .replace(/[\u200B\u00A0\s]/g, "")
        .trim();

      if (!textContent || textContent.length < 1) {
        cleanedText = removeParagraphTags(userInput.replaceAll("&nbsp;", ""));
      }

      cleanedText = cleanedText.replace(/[\u200B\uFEFF]/g, "");

      cleanedText = cleanedText
        .replace(/<\/prosody>/gi, (match, offset, string) => {
          const beforeText = string.substring(0, offset);
          const openCount = (beforeText.match(/<prosody[^>]*>/gi) || []).length;
          const closeCount = (beforeText.match(/<\/prosody>/gi) || []).length;
          return openCount > closeCount ? match : "";
        })
        .replace(/<\/phoneme>/gi, (match, offset, string) => {
          const beforeText = string.substring(0, offset);
          const openCount = (beforeText.match(/<phoneme[^>]*>/gi) || []).length;
          const closeCount = (beforeText.match(/<\/phoneme>/gi) || []).length;
          return openCount > closeCount ? match : "";
        });

      const hasProsodyOrPhoneme =
        /<prosody[^>]*>/i.test(cleanedText) ||
        /<phoneme[^>]*>/i.test(cleanedText);

      let text: string;
      if (finalText) {
        text = finalText;
      } else if (hasProsodyOrPhoneme) {
        text = getSanitizedInput(stripSpeakTags(cleanedText));
      } else {
        text = getWrappedInput(stripSpeakTags(cleanedText));
      }

      text = cleanupSSML(text);
      text = sanitizePhInSsml(text);

      // Validate
      const validation = validateSSML(text, false, t, {
        dynamicAllowedValues: { voiceNames, styleNames: emotionNames, languageValues },
      });

      if (!validation.valid) {
        if (validation.errors) {
          setValidationErrors(validation.errors);
          throw new Error(validation.cumulativeErrors.messages[0]);
        }

        // Attempt auto-fix
        let fixed = cleanedText;
        const fixTagType = (tagName: string) => {
          const openRegex = new RegExp(`<${tagName}[^>]*>`, "g");
          const closeRegex = new RegExp(`<\\/${tagName}>`, "g");
          const opens = (fixed.match(openRegex) || []).length;
          const closes = (fixed.match(closeRegex) || []).length;

          if (closes > opens) {
            let closesToRemove = closes - opens;
            fixed = fixed.replace(closeRegex, (match) => {
              if (closesToRemove > 0) {
                closesToRemove--;
                return "";
              }
              return match;
            });
          }
          if (opens > closes) {
            const missingCloses = opens - closes;
            for (let i = 0; i < missingCloses; i++) {
              if (fixed.includes("</speak>")) {
                fixed = fixed.replace("</speak>", `</${tagName}></speak>`);
              } else {
                fixed += `</${tagName}>`;
              }
            }
          }
        };

        fixTagType("prosody");
        fixTagType("phoneme");

        const retryValidation = validateSSML(fixed, false, t, {
          dynamicAllowedValues: { voiceNames, styleNames: emotionNames, languageValues },
        });

        if (retryValidation.valid) {
          return fixed;
        }

        throw new Error(
          validation.cumulativeErrors.messages[0] || t("tts.validation.invalidSSML")
        );
      }

      return text;
    },
    [userInput, selectedTextForPlay, voiceNames, emotionNames, languageValues, t, setValidationErrors]
  );

  // If finalText is given, that includes speak tag so skip the other checks
  const getTTSRequestUrl = useCallback(
    async (
      shouldUseFullText: boolean,
      customSampleRate = "",
      finalText = "",
      voiceOverride?: string,
      languageOverride?: string
    ) => {
      setValidationErrors([]);

      if (!token && !runtimeConfig.ttsOnPrem) {
        throw new Error("No authentication token provided");
      }

      const voiceToUse = voiceOverride || selectedVoice;
      const languageToUse = languageOverride || selectedLanguage;

      try {
        const rawText =
          selectedTextForPlay && !shouldUseFullText
            ? selectedTextForPlay
            : userInput;
        const afterNbspReplacement = rawText.replaceAll("&nbsp;", "");
        let cleanedText = removeParagraphTags(afterNbspReplacement);
        // Check if the selected text has actual content
        const textContent = cleanedText
          .replace(/<[^>]*>/g, "")
          .replace(/[\u200B\u00A0\s]/g, "")
          .trim();
        // If no content and we were using selected text, fall back to full text
        if (!textContent || textContent.length < 1) {
          cleanedText = removeParagraphTags(userInput.replaceAll("&nbsp;", ""));
        }

        // Strip zero-width spaces and non-breaking spaces
        cleanedText = cleanedText.replace(/[\u200B\uFEFF]/g, "");

        // Clean up stray closing tags before processing
        cleanedText = cleanedText
          .replace(/<\/prosody>/gi, (match, offset, string) => {
            // Only keep closing tags that have a matching opening tag before them
            const beforeText = string.substring(0, offset);
            const openCount = (beforeText.match(/<prosody[^>]*>/gi) || [])
              .length;
            const closeCount = (beforeText.match(/<\/prosody>/gi) || []).length;
            return openCount > closeCount ? match : "";
          })
          .replace(/<\/phoneme>/gi, (match, offset, string) => {
            // Only keep closing tags that have a matching opening tag before them
            const beforeText = string.substring(0, offset);
            const openCount = (beforeText.match(/<phoneme[^>]*>/gi) || [])
              .length;
            const closeCount = (beforeText.match(/<\/phoneme>/gi) || []).length;
            return openCount > closeCount ? match : "";
          });

        const prosodyTagRegex = /<prosody[^>]*>/i;
        const phonemeTagRegex = /<phoneme[^>]*>/i;
        const hasProsodyOrPhoneme =
          prosodyTagRegex.test(cleanedText) ||
          phonemeTagRegex.test(cleanedText);

        let text: string;
        if (finalText) {
          text = finalText;
        } else if (hasProsodyOrPhoneme) {
          // Strip any existing <speak> tags before getSanitizedInput
          text = getSanitizedInput(stripSpeakTags(cleanedText));
        } else {
          // Strip any existing <speak> tags before getWrappedInput
          text = getWrappedInput(stripSpeakTags(cleanedText));
        }

        text = cleanupSSML(text);
        text = sanitizePhInSsml(text);

        // Validate SSML before synthesis
        const validation = validateSSML(text, false, t, {
          dynamicAllowedValues: { voiceNames, styleNames: emotionNames, languageValues }
        });
        if (!validation.valid) {
          if (validation.errors) {
            setValidationErrors(validation.errors);
            // setErrorMessage(validation.cumulativeErrors.messages[0]);
            throw new Error(validation.cumulativeErrors.messages[0]);
          }
          const processTags = [
            // Enhanced tag fixing that handles both prosody and phoneme tags
            () => {
              let fixed = cleanedText;
              const fixTagType = (tagName: string) => {
                const openRegex = new RegExp(`<${tagName}[^>]*>`, "g");
                const closeRegex = new RegExp(`<\\/${tagName}>`, "g");
                const opens = (fixed.match(openRegex) || []).length;
                const closes = (fixed.match(closeRegex) || []).length;

                if (closes > opens) {
                  let closesToRemove = closes - opens;
                  fixed = fixed.replace(closeRegex, (match) => {
                    if (closesToRemove > 0) {
                      closesToRemove--;
                      return "";
                    }
                    return match;
                  });
                }

                // Add missing closing tags if needed
                if (opens > closes) {
                  const missingCloses = opens - closes;
                  for (let i = 0; i < missingCloses; i++) {
                    if (fixed.includes("</speak>")) {
                      fixed = fixed.replace(
                        "</speak>",
                        `</${tagName}></speak>`
                      );
                    } else {
                      fixed += `</${tagName}>`;
                    }
                  }
                }
              };

              fixTagType("prosody");
              fixTagType("phoneme");
              return fixed;
            },
          ];

          for (let i = 0; i < processTags.length; i++) {
            try {
              const processedText = processTags[i]();
              const validation = validateSSML(processedText, false, t, {
                dynamicAllowedValues: { voiceNames, styleNames: emotionNames, languageValues }
              });
              if (validation.valid) {
                const { response, fromCache } = await synthesize(
                  processedText,
                  token,
                  customSampleRate,
                  voiceToUse,
                  languageToUse
                );
                const audioBytes = new Uint8Array(response.audio);
                updateWavHeader(audioBytes);
                const audioBlob = new Blob([audioBytes], {
                  type: "audio/wav",
                });
                return { url: URL.createObjectURL(audioBlob), fromCache };
              }
            } catch (processingError) {
              continue;
            }
          }
          throw new Error(
            validation.cumulativeErrors.messages[0] ||
            t("tts.validation.invalidSSML")
          );
        }

        const { response, fromCache } = await synthesize(
          text,
          token,
          customSampleRate,
          voiceToUse,
          languageToUse
        );
        const audioBytes = new Uint8Array(response.audio);
        updateWavHeader(audioBytes);
        const audioBlob = new Blob([audioBytes], {
          type: "audio/wav",
        });
        return { url: URL.createObjectURL(audioBlob), fromCache };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("TTS synthesis failed:", error);
        if (error instanceof Error) {
          if (error.message.includes("No authentication token provided")) {
            setErrorMessage(t("notifications.authenticationRequired"));
          } else if (
            error.message === "QUOTA_EXHAUSTED" ||
            isQuotaExhaustedError(error)
          ) {
            // Handle quota exhaustion with different messages based on scenario
            const originalError = (error as { originalError?: unknown })
              .originalError;
            const quotaInfo = extractQuotaInfo(originalError || error);

            if (quotaInfo && isUsagePeriodExpired(quotaInfo)) {
              // Usage period expired (totalQuota = 0.0)
              setErrorMessage(t("tts.usagePeriodExpiredMessage"));
            } else {
              // Fallback to generic message if no quota info available
              setErrorMessage(t("tts.quotaExhaustedMessage"));
            }
          } else {
            // setErrorMessage(error.message || t("tts.generalErrorMessage"));
          }
        }
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, userInput, selectedTextForPlay, synthesize, selectedVoice]
  );


  // This function uses WebAudioAPI instead of HTMLAudioElement to stream the audio.
  const streamTTSAudio = useCallback(
    async (
      shouldUseFullText: boolean,
      callbacks: {
        onPlaybackStarted: () => void;
        onPlaybackEnded: () => void;
      },
      customSampleRate = "",
      finalText = "",
      voiceOverride?: string,
      languageOverride?: string
    ): Promise<{
      stop: () => void;
      pause: () => void;
      resume: () => void;
      isPaused: () => boolean;
      analyser: AnalyserNode;
    }> => {
      setValidationErrors([]);

      if (!token && !runtimeConfig.ttsOnPrem) {
        throw new Error("No authentication token provided");
      }

      const voiceToUse = voiceOverride || selectedVoice;
      const languageToUse = languageOverride || selectedLanguage;

      try {
        const text = prepareSSMLText(shouldUseFullText, finalText);
        const sampleRate = +(customSampleRate || selectedSampleRate) || 22050;

        //Check if we already have this audio cached
        const cached = checkCache(text, customSampleRate, voiceToUse, languageToUse);
        if (cached) {
          const audioContext = new AudioContext({ sampleRate });
          const analyser = audioContext.createAnalyser();
          analyser.connect(audioContext.destination);

          // Convert cached WAV to float32 (skip 44-byte header)
          const audioBytes = new Uint8Array(cached.audio);
          const pcmData = new DataView(audioBytes.buffer, 44);
          const float32 = new Float32Array(pcmData.byteLength / 2);
          for (let i = 0; i < float32.length; i++) {
            float32[i] = pcmData.getInt16(i * 2, true) / 32768;
          }

          const audioBuffer = audioContext.createBuffer(1, float32.length, sampleRate);
          audioBuffer.copyToChannel(float32, 0);

          const source = audioContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(analyser);

          let stopped = false;

          source.onended = () => {
            if (!stopped) {
              callbacks.onPlaybackEnded();
              audioContext.close().catch(() => {});
            }
          };

          callbacks.onPlaybackStarted();
          source.start(0);

          return {
            stop: () => {
              stopped = true;
              source.stop();
              audioContext.close().catch(() => {});
            },
            pause: () => { audioContext.suspend().catch(() => {}); },
            resume: () => {
              audioContext.resume().catch(() => {});
            },
            isPaused: () => audioContext.state === "suspended",
            analyser,
          };
        }

        // No cache hit — stream from server, collect chunks for caching
        const rawChunksForCache: Uint8Array[] = [];

        const audioContext = new AudioContext({ sampleRate });
        const analyser = audioContext.createAnalyser();
        analyser.connect(audioContext.destination);
        let nextStartTime = 0;
        let isFirstChunk = true;
        let stopped = false;
        let playbackStartedCalled = false;
        let cancelStream: (() => void) | null = null;
        let streamEnded = false;
        // eslint-disable-next-line prefer-const
        let startupTimeout: ReturnType<typeof setTimeout>;

        const pendingChunks: Float32Array<ArrayBuffer>[] = [];
        let isScheduling = false;
        // Small lookahead so pause/stop stay responsive (this look ahead time decides how long of a queue do we keep processed)
        const SCHEDULE_AHEAD_TIME = 0.2;

        // Assemble collected chunks and store in cache
        const cacheCollectedChunks = () => {
          if (rawChunksForCache.length === 0) return;
          const totalLength = rawChunksForCache.reduce((acc, c) => acc + c.length, 0);
          const combined = new Uint8Array(totalLength);
          let off = 0;
          for (const chunk of rawChunksForCache) {
            combined.set(chunk, off);
            off += chunk.length;
          }
          setCache(text, { audio: combined, phoneme: "", debugInfo: undefined }, customSampleRate, voiceToUse, languageToUse);
        };

        const scheduleChunks = () => {
          if (isScheduling || stopped) return;
          isScheduling = true;

          while (pendingChunks.length > 0) {
            if (stopped) break;

            const currentTime = audioContext.currentTime;
            const startTime = Math.max(currentTime, nextStartTime);

            // Don't schedule too far ahead for play And pause to work correctly else there will be delays and overlaps
            if (startTime > currentTime + SCHEDULE_AHEAD_TIME && nextStartTime > currentTime) {
              break;
            }

            const float32 = pendingChunks.shift()!;
            const audioBuffer = audioContext.createBuffer(1, float32.length, sampleRate);
            audioBuffer.copyToChannel(float32, 0);

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(analyser);
            source.start(startTime);
            nextStartTime = startTime + audioBuffer.duration;

            // Each source ending triggers the next batch
            source.onended = () => {
              if (!stopped) {
                scheduleChunks();

                if (streamEnded && pendingChunks.length === 0) {
                  // Save to cache before signaling completion
                  cacheCollectedChunks();
                  callbacks.onPlaybackEnded();
                  audioContext.close().catch(() => { });
                }
              }
            };
          }

          isScheduling = false;
        };

        const stop = () => {
          stopped = true;
          clearTimeout(startupTimeout);
          cancelStream?.();
          pendingChunks.length = 0;
          audioContext.close().catch(() => { });
        };

        const pause = () => {
          if (audioContext.state === "running") {
            audioContext.suspend().catch(() => { });
          }
        };

        const resume = () => {
          if (audioContext.state === "suspended") {
            audioContext.resume().then(() => {
              nextStartTime = Math.max(audioContext.currentTime, nextStartTime);
              scheduleChunks();
            }).catch(() => { });
          }
        };

        const isPaused = () => audioContext.state === "suspended";

        // Returns synchronously
        const { cancel } = synthesizeStream(
          text,
          token,
          {
            onChunk: (audioData: Uint8Array, isFirst: boolean) => {
              if (stopped) return;

              // Collect raw chunk before any processing
              rawChunksForCache.push(new Uint8Array(audioData));

              let pcmData = audioData;
              
              // Strip 44-byte WAV header from first chunk
              if (isFirstChunk && isFirst) {
                pcmData = pcmData.slice(44);
                isFirstChunk = false;
              }

              if (pcmData.length === 0) return;

              // Convert 16-bit PCM to float32 for Web Audio API
              const numSamples = pcmData.length / 2;
              const float32 = new Float32Array(numSamples) as Float32Array<ArrayBuffer>;
              const dataView = new DataView(
                pcmData.buffer,
                pcmData.byteOffset,
                pcmData.byteLength
              );

              for (let i = 0; i < numSamples; i++) {
                const int16 = dataView.getInt16(i * 2, true);
                float32[i] = int16 / 32768;
              }

              pendingChunks.push(float32);
              scheduleChunks();

              if (!playbackStartedCalled) {
                clearTimeout(startupTimeout);
                callbacks.onPlaybackStarted();
                playbackStartedCalled = true;
              }
            },
            onEnd: () => {
              if (stopped) return;

              if (!playbackStartedCalled) {
                clearTimeout(startupTimeout);
                audioContext.close().catch(() => { });
                setErrorMessage(t("tts.playbackError"));
                callbacks.onPlaybackEnded();
                return;
              }

              streamEnded = true;

              if (pendingChunks.length > 0) {
                scheduleChunks();
              } else {
                // Wait for last scheduled source to finish playing
                const remainingTime = nextStartTime - audioContext.currentTime;
                if (remainingTime > 0) {
                  setTimeout(() => {
                    if (!stopped) {
                      // Save to cache before signaling completion
                      cacheCollectedChunks();
                      callbacks.onPlaybackEnded();
                      audioContext.close().catch(() => { });
                    }
                  }, remainingTime * 1000);
                } else {
                  // Save to cache before signaling completion
                  cacheCollectedChunks();
                  callbacks.onPlaybackEnded();
                  audioContext.close().catch(() => { });
                }
              }
            },
            onError: (error: Error) => {
              if (stopped) return;
              clearTimeout(startupTimeout);
              stopped = true;
              audioContext.close().catch(() => { });

              if (isQuotaExhaustedError(error)) {
                const quotaInfo = extractQuotaInfo((error as { originalError?: unknown }).originalError || error);
                if (quotaInfo && isUsagePeriodExpired(quotaInfo)) {
                  setErrorMessage(t("tts.usagePeriodExpiredMessage"));
                } else {
                  setErrorMessage(t("tts.quotaExhaustedMessage"));
                }
              } else {
                setErrorMessage(t("tts.playbackError"));
              }

              callbacks.onPlaybackEnded();
            },
          },
          customSampleRate,
          voiceToUse,
          languageToUse
        );

        cancelStream = cancel;

        startupTimeout = setTimeout(() => {
          if (!playbackStartedCalled && !stopped) {
            stopped = true;
            cancelStream?.();
            pendingChunks.length = 0;
            audioContext.close().catch(() => { });
            setErrorMessage(t("tts.playbackError"));
            callbacks.onPlaybackEnded();
          }
        }, 5000);

        return { stop, pause, resume, isPaused, analyser };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("TTS streaming failed:", error);
        if (error instanceof Error) {
          if (error.message.includes("No authentication token provided")) {
            setErrorMessage(t("notifications.authenticationRequired"));
          } else if (
            error.message === "QUOTA_EXHAUSTED" ||
            isQuotaExhaustedError(error)
          ) {
            const originalError = (error as { originalError?: unknown }).originalError;
            const quotaInfo = extractQuotaInfo(originalError || error);
            if (quotaInfo && isUsagePeriodExpired(quotaInfo)) {
              setErrorMessage(t("tts.usagePeriodExpiredMessage"));
            } else {
              setErrorMessage(t("tts.quotaExhaustedMessage"));
            }
          }
        }
        throw error;
      }
    },
    [token, synthesizeStream, checkCache, setCache, selectedVoice, selectedLanguage, selectedSampleRate, prepareSSMLText, t, setValidationErrors, setErrorMessage]
  );

  const checkIsNestingValid = (
    editorRef: React.RefObject<EditorRef | null>,
    tagToCheck: string,
    isProsody: boolean,
    tagNameForError?: string
  ): boolean => {
    const isInvalidNestedTag = isProsody
      ? checkInvalidNestedTagForProsody(editorRef)
      : checkInvalidNestedTag(editorRef, tagToCheck);

    if (isInvalidNestedTag.shouldShowError) {
      if (isInvalidNestedTag.isWrappedWithSameTag) {
        setErrorMessage(
          t("notifications.ttsSameTagNested", {
            tag: isProsody ? "prosody" : tagNameForError || tagToCheck,
          })
        );
      } else if (isInvalidNestedTag.isWrappedWithSayAsTag) {
        setErrorMessage(t("notifications.ttsNestedInSayAsTag"));
      }
      setShouldCloseMenu(true);
      return false;
    }
    return true;
  };

  const getPhonemeText = async (input: string) => {
    const phoneme = await getPhoneme(
      token,
      input,
      selectedLanguage,
      selectedVoice,
      selectedSampleRate,
      selectedEnv,
      selectedDeployment
    );
    return phoneme;
  };

  return { getTTSRequestUrl, checkIsNestingValid, getPhonemeText, streamTTSAudio };
}